import {
  ActivityFooterOrder,
  ActivityFooterTabState,
  useActivityFooterStore
} from '../../../../stores/activityFooterStore';

import { useHotelIdsFromStatus } from '../utils/useHotelIdsFromStatus';
import { useActivityInformationRenderSwitches } from '../utils/useActivityInformationRenderSwitches';
import { FooterTabsControlled, FooterTabType } from '../../../../components/Modal/FooterTabs';
import { useContext, useMemo } from 'react';
import { HotelBookingType } from '../../../../globalModels/common/hotelBookingType.ts';
import { ActivityInformationCompanyDisplay } from '../displays/activityInformationCompanyDisplay';
import { Box } from '@chakra-ui/react';
import { ActivityInformationTravellerDisplay } from '../displays/activityInformationTravellerDisplay';
import { ActivityInformationHotelDisplay } from '../displays/activityInformationHotelDisplay';
import { CompanySearch } from '../search/CompanySearch';
import { HotelSearch } from '../search/HotelSearch';
import { TravellerSearch } from '../search/TravellerSearch';
import { ActivityInformationContext } from './activityInformationContext';
import { useAuth } from '@invara-dev/react-base';
import { useActivityBookingFlagsQuery, useActivityTravellerOptionsQuery } from '../../api/activityStatusQueries';

/**
 * ActivityInformationFooter component renders a set of controlled footer tabs showing
 * details about the current activity, such as company, travelers, and hotel information,
 * as well as search panels for travelers, companies, and hotels.
 *
 * It performs the following responsibilities:
 * 1. Uses `useAuth` to check if the user is authenticated; renders nothing if not.
 * 2. Subscribes to the current activity ID from `ActivityInformationContext`.
 * 3. On activity ID change, invalidates stale react-query caches for the previous ID
 *    and triggers a refetch of the new activity's status.
 * 4. Extracts hotel and traveler IDs from the fetched status data.
 * 5. Memoizes the tab definitions (`tabs`) to avoid unnecessary recalculation.
 * 6. Renders the `FooterTabsControlled` component with its disclosure and index state
 *    managed by `useActivityFooterStore`.
 *
 * @component
 * @returns {JSX.Element} A set of footer tabs displaying activity information, or an empty fragment if unauthenticated.
 */
export const ActivityInformationFooter = () => {
  const auth = useAuth();
  const context = useContext(ActivityInformationContext);

  const bookingFlags = useActivityBookingFlagsQuery(context?.activityId);
  const travellerOptions = useActivityTravellerOptionsQuery(context?.activityId);
  const store = useActivityFooterStore();

  // Note: Activity sync is now handled automatically by ActivityInformationContextProvider
  // when EditSingleActivity calls setActivityId()

  const hotelIds = useHotelIdsFromStatus(bookingFlags.data);
  const travellerIds = useMemo(
    () => travellerOptions.data?.Travellers.flatMap((x) => x.Id) ?? [],
    [travellerOptions.data?.Travellers]
  );

  const renderSwitches = useActivityInformationRenderSwitches({
    companyId: bookingFlags.data?.CompanyId,
    hotelIds,
    travellerIds,
    approvedBookerId: travellerOptions.data?.ApprovedBookerId,
    requestedById: travellerOptions.data?.RequestedById,
    hotelBookingType: bookingFlags.data?.HotelBookingType
  });

  // Memoize tabs to prevent unnecessary recalculations
  const tabs: FooterTabType[] = useMemo(
    () =>
      ActivityFooterOrder.flatMap((tab) => {
        switch (tab) {
          case ActivityFooterTabState.Company:
            return {
              header: 'Activity Company',
              disabled: !renderSwitches.renderPolicies,
              panel: renderSwitches.renderPolicies &&
                bookingFlags.data?.CompanyId &&
                bookingFlags.data?.HotelBookingType != HotelBookingType.Personal && (
                  <ActivityInformationCompanyDisplay
                    companyId={bookingFlags.data?.CompanyId}
                    flsq={bookingFlags.data?.Flsq}
                  />
                )
            };
          case ActivityFooterTabState.Travellers:
            return {
              header: 'Activity Travelers',
              disabled: !renderSwitches.renderTravellers,
              panel: renderSwitches.renderTravellers && (
                <ActivityInformationTravellerDisplay
                  companyId={bookingFlags.data?.CompanyId}
                  requestedById={travellerOptions.data?.RequestedById}
                  approvedBookerId={travellerOptions.data?.ApprovedBookerId}
                  travellerIds={travellerIds}
                  flsq={bookingFlags.data?.Flsq}
                />
              )
            };
          case ActivityFooterTabState.HotelInfo:
            return {
              header: 'Activity Hotel',
              disabled: !renderSwitches.renderHotel,
              panel: renderSwitches.renderHotel && (
                <ActivityInformationHotelDisplay hotelIds={hotelIds ?? []} flsq={bookingFlags.data?.Flsq} />
              )
            };
          case ActivityFooterTabState.TravellersSearch:
            return {
              header: 'Traveler Search',
              panel: (
                <Box>
                  <TravellerSearch companyId={bookingFlags.data?.CompanyId} />
                </Box>
              )
            };
          case ActivityFooterTabState.CompanySearch:
            return {
              header: 'Company Search',
              panel: (
                <Box>
                  <CompanySearch />
                </Box>
              )
            };
          case ActivityFooterTabState.HotelSearch:
            return {
              header: 'Hotel Search',
              panel: (
                <Box>
                  <HotelSearch />
                </Box>
              )
            };
          default:
            return [];
        }
      }),
    [renderSwitches, bookingFlags.data, travellerOptions.data, travellerIds]
  );

  if (!auth.isAuthenticated) {
    return <></>;
  }

  return (
    <>
      <FooterTabsControlled
        height={store.height}
        setHeight={store.setHeight}
        disclosure={{
          onOpen: store.onOpen,
          onClose: store.onClose,
          open: store.open,
          onToggle: store.onToggle,
          setOpen: store.setOpen
        }}
        index={store.index}
        setIndex={store.setIndex}
        tabs={tabs}
      />
    </>
  );
};
